import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { MultiSelect } from "primereact/multiselect";

import {
    getDistinctPingprobeAssetTypeInit,
    getPingprobeInventoryInit,
    getPingprobeAssetLastPingTimeInit,
    getPingprobeCriticalAssetsInit,
} from "../redux/actions";
import SelectedChips from "../../../common/components/selected-chips/SelectedChips";
import { parseDateTime } from "../../../common/date-time";
import PingProbeIncidentDialog from "./PingProbeIncidentDialog";
import { tableFilters } from "../constants";
import "./PingProbeInventory.scss";


/**
 * Functional Component to show PingProbe Inventory
 * @returns JSX
 */
const PingProbeInventory = () => {
    const assetTypes = useSelector((state) => state.pingprobe.assetTypes);
    const assetList = useSelector((state) => state.pingprobe.assetList);
    const totalRecords = useSelector(state => state.pingprobe.totalRecords); // Redux total records
    const allCriticalAssets = useSelector(state => state.pingprobe.allCriticalAssets); // Redux total records

    const pingTime = useSelector((state) => state.pingprobe.pingTime);
    const dispatch = useDispatch();
    const [selectedAssetTypes, setSelectedAssetTypes] = useState([]);
    const [showCriticalAssetList, setShowCriticalAssetList] = useState(false);
    const [isCriticalViewActive, setIsCriticalViewActive] = useState(false);
    const [selectedIpAddress, setSelectedIpAddress] = useState(null);
    const inventoryTableRef = useRef(null);
    const [filterCondition, setFilterCondition] = useState(""); // **Added filterCondition state**


    const [lazyParams, setLazyParams] = useState({
        first: 0, // Starting index
        rows: 30, // Default page size
        page: 0, // Current page number
        sortField: "incidentNumber", //"ipAddress", // Default sort field
        sortOrder: -1, // Default sort order (-1 for descending, 1 for ascending)
    });

    /**
     * Fetching distinct asset type list
     */
    useEffect(() => {
        if (
            !assetTypes ||
            (Array.isArray(assetTypes) && assetTypes.length === 0)
        ) {
            dispatch(getDistinctPingprobeAssetTypeInit());
        }
    }, []);

    /**
     * Fetching asset detail for all asset types
     */
    useEffect(() => {
        if (Array.isArray(assetTypes)) {
            setSelectedAssetTypes([...assetTypes]);
            if (assetTypes.length > 0) {
                fetchInventoryDetail(true);
            }
        }
    }, [assetTypes]);

    useEffect(() => {
        if (selectedAssetTypes.length > 0) {
            const joined = selectedAssetTypes.join(",");
            dispatch(getPingprobeCriticalAssetsInit(joined));
           
        }
    }, [selectedAssetTypes]);

    /**
     * Creating list of Critical Assets
     */
    useEffect(() => {
        dispatch(getPingprobeAssetLastPingTimeInit());
    }, [assetList]);


    useEffect(() => {
        const incidentFilter = isCriticalViewActive ? "incidentStatus&in&New:In Progress" : ""; 
        const currentFilters = [constructFilterParams(filterCondition), incidentFilter]
            .filter(Boolean)
            .join(",");
     
        fetchInventoryDetail(false, { ...lazyParams, first: 0, page: 0 }, currentFilters); 
    }, [isCriticalViewActive]); 

    const getCombinedFilters = (filters) => {
        const incidentFilter = isCriticalViewActive ? "incidentStatus&in&New:In Progress" : "";
        return [constructFilterParams(filters), incidentFilter].filter(Boolean).join(",");
      };

    /**
     * Function to load asset list
     * @param {boolean} initialLoad
     */
    const fetchInventoryDetail = (initialLoad,params=lazyParams,currentFilters="") => {
        const sortDirection= params.sortOrder== -1?'DESC' : 'ASC';
        let selectedAssetTypeList = selectedAssetTypes.join(",");
        if (selectedAssetTypes.length === 0 && initialLoad !== true) {
            return;
        }
        else if (initialLoad === true) {
            selectedAssetTypeList = assetTypes.join(",");
        }
       

        dispatch(getPingprobeInventoryInit(
            selectedAssetTypeList,
            params.page, // Current page number
            params.rows, // Number of rows per page
            params.sortField, // Sorting field
            sortDirection, // Sorting order
           currentFilters
        ));
    };


    const constructFilterParams = (filterConditions) => {
        if (!filterConditions || typeof filterConditions !== "object") {
          return ""; // Return empty string if no filters are present
        }
      
        const queryParams = [];
      
        Object.keys(filterConditions).forEach((key) => {
          const filter = filterConditions[key];
          const constraints = filter?.constraints;
      
          if (constraints && constraints.length > 0) {
            const { value, matchMode } = constraints[0];
      
            if (value !== undefined && value !== null && value !== "") {
              queryParams.push(
                `${encodeURIComponent(key)}&${encodeURIComponent(matchMode)}&${encodeURIComponent(value)}`
              );
            }
          }
        });
      
        return queryParams.join(",");
      };

    /**
     * Function to export table data
     */
    const exportInventoryDetail = () => inventoryTableRef.current.exportCSV();

    /**
     * Function to toogle show critical asset flag
     */
    const toogleShowCriticalAssetList = () => {
        const newState = !showCriticalAssetList;
        setShowCriticalAssetList(newState);
        setIsCriticalViewActive(newState);
 
        const updatedParams = { ...lazyParams, first: 0, page: 0 };
        setLazyParams(updatedParams);
    };

    const incidentTemplate = ({
        incidentNumber,
        incidentStatus,
        incidentTimestamp,
    }) => {
        const incidentContainerClassName = ["New", "In Progress"].includes(
            incidentStatus
        )
            ? "active"
            : "";
        return (
            <div className={incidentContainerClassName}>
                {incidentNumber === null ? (
                    "-"
                ) : (
                    <span>
                        {incidentNumber} <br />
                        {parseDateTime(incidentTimestamp)}
                    </span>
                )}
            </div>
        );
    };

    const incidentLogsTemplate = ({ ipAddress }) => (
        <Button
            icon="pi pi-info-circle"
            className="p-button-text p-button-secondary"
            onClick={() => setSelectedIpAddress(ipAddress)}
        />
    );

    const severityTemplate = ({severity}) => 
         severity ? 
         (severity) : 
         (<span>-</span>);
    
    const assignmentGroupTemplate = ({assignmentGroup}) => 
        assignmentGroup ? (
            assignmentGroup
        ) : (
            <span>-</span>
        );


        /**
     * Handle filtering event
     */
    const onFilter = (filters) => {
        setFilterCondition(filters);
        const updatedParams={
            ...lazyParams, 
              first: 0,
              page: 0
        };
        setLazyParams(updatedParams); // Reset to first page
        const currentFilters = getCombinedFilters(filters);
        fetchInventoryDetail(false,updatedParams,currentFilters); // Fetch filtered data
    };

    /**
     * Handle pagination event
     */
    const onPage = (event) => {
        console.log( "pageevnt",event);
        const updatedParams=
        {
            ...lazyParams,
            first: event.first, // Start index
            rows: event.rows, // Rows per page
            page: event.page // Current page number
        };
        setLazyParams(updatedParams);
            const currentFilters = getCombinedFilters(filterCondition);
            fetchInventoryDetail(false,updatedParams,currentFilters); // Fetch data for the new page
    };

    /**
     * Handle sorting event
     */
    const onSort = (event) => {
       const updatedParams={
        ...lazyParams,
        sortField: event.sortField, // Sorting field
        sortOrder: event.sortOrder // Sorting order
    };
        setLazyParams(updatedParams);
        const currentFilters = getCombinedFilters(filterCondition);
        fetchInventoryDetail(false,updatedParams,currentFilters); // Fetch sorted data
    };

const tableData =  assetList;
const recordCount = totalRecords;    
    return (
        <div className="pingprobe-inventory-container container-fluid">
            <PingProbeIncidentDialog
                selectedIpAddress={selectedIpAddress}
                setSelectedIpAddress={setSelectedIpAddress}
            />
            <p className="page-label">
                Ping Probe
                <i className="pi pi-angle-right" />
                Inventory
            </p>
            <div className="pingprobe-inventory-action row">
                <div className="col-12 col-lg-6 input-field">
                    <label htmlFor="assetTypes">Asset Types</label>
                    <MultiSelect
                        id="assetTypes"
                        value={selectedAssetTypes}
                        options={assetTypes}
                        onChange={(e) => setSelectedAssetTypes(e.value)}
                        filter
                    />
                </div>
                <SelectedChips
                    label="Selected Asset Types"
                    selectedValues={selectedAssetTypes}
                    setSelectedValues={setSelectedAssetTypes}
                />
                <div className="col-12">
                    <Button
                        label="Get Inventory"
                        onClick={()=>{
                            setShowCriticalAssetList(false);
                            setIsCriticalViewActive(false);
                            fetchInventoryDetail();
                        }}
                        disabled={selectedAssetTypes.length === 0}
                    />
                </div>
            </div>
            <hr />
            <div className="pingprobe-inventory-content">
                <div className="pingprobe-inventory-table-action">
                    <Button
                        icon="pi pi-refresh"
                        className="p-button-text"
                        onClick={fetchInventoryDetail}
                    />
                    <Button
                        icon="pi pi-download"
                        className="p-button-text"
                        onClick={exportInventoryDetail}
                    />
                    <Button
                        icon="pi pi-exclamation-triangle"
                        className={`${
                            isCriticalViewActive ? "" : "p-button-text"
                        } p-button-danger`}
                       // label={"" + criticalAssetList.length}
                       label={"" + allCriticalAssets.length}
                        onClick={toogleShowCriticalAssetList}
                    />
                    <small>
                        Last pinged at{" "}
                        {!!pingTime ? parseDateTime(pingTime) : "-"}
                    </small>
                </div>
                <DataTable
                    className="pingprobe-inventory-table"
                    ref={inventoryTableRef}
                    value={
                        //showCriticalAssetList ? criticalAssetList : assetList
                    tableData
                    }
                    filters={tableFilters}
                    filterDisplay="menu"
                   // paginator
                   onFilter={(e) => onFilter(e.filters)} // **Handle filtering event**
                    sortField={lazyParams.sortField} // Dynamic sort field
                    sortOrder={lazyParams.sortOrder} // Dynamic sort order
                    lazy={!showCriticalAssetList}
                    paginator
                    totalRecords={recordCount} // Dynamic total records from Redux
                    first={lazyParams.first} // Start index
                    rows={lazyParams.rows} // Rows per page
                    rowsPerPageOptions={[30, 50, 100]} // Page size options
                    onPage={onPage} // Pagination handler
                    onSort={onSort} // Sorting handler
                    paginatorTemplate="CurrentPageReport RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink "
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                    emptyMessage="No Asset found."
                >
                    <Column
                        header="IP Address"
                        field="ipAddress"
                        sortable
                        filter
                    />
                    <Column
                        header="Asset Type"
                        field="assetType"
                        sortable
                        filter
                    />
                    <Column
                        header="Asset Name"
                        field="assetName"
                        sortable
                        filter
                    />
                    <Column header="DNS Name" field="dnsName" sortable filter />
                    <Column
                        header="Location"
                        field="location"
                        sortable
                        filter
                    />
                    <Column
                        header="Last Incident (In 90 days)"
                        field="incidentNumber"
                        body={incidentTemplate}
                        sortable
                    />
                    <Column
                        header="Severity"
                        field="severity"
                        body={severityTemplate}
                        sortable
                        filter
                    />
                    <Column
                        header="Assignment Group"
                        field="assignmentGroup"
                        body={assignmentGroupTemplate}
                        sortable
                        filter
                    />
                    <Column header="" body={incidentLogsTemplate} />
                </DataTable>
            </div>
        </div>
    );
};

export default PingProbeInventory;
